import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import DetailsModal from '@/components/DetailsModal';
import ExploreJourneys from '@/components/ExploreJourneys';
import { Settings, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GoalConversions = ({ websiteId, period = '24h', startDate, endDate, channel, location, device, os, browser, goal, activeGoalFilter, token, pageFilters = [], existingFilters = [] }) => {
  const navigate = useNavigate();

  // Auth header helper — token is null in publicMode (PublicAnalyticsPage at
  // /site/:domain). The /goals, /funnels, /funnels/.../stats, /goal-conversions
  // and /property-events endpoints now accept unauthenticated reads on
  // is_public sites via get_current_user_optional + check_website_permission's
  // public bypass, so we just omit the Authorization header when no token.
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Create stable string keys for dependencies to prevent unnecessary re-renders
  const pageFiltersKey = useMemo(() => JSON.stringify(pageFilters), [pageFilters]);
  const filtersKey = useMemo(() => existingFilters.join('|'), [existingFilters]);
  
  // Ref to track if we're currently fetching to prevent duplicate calls
  const fetchingRef = useRef(false);
  const lastFetchKeyRef = useRef('');
  
  const [activeTab, setActiveTab] = useState('goals');
  const [goals, setGoals] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [funnelStats, setFunnelStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [limit, setLimit] = useState(9);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'unique_visitors', direction: 'desc' });
  
  // Properties state
  const [propertyEvents, setPropertyEvents] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  // Handle goal click for filtering
  const handleGoalClick = (goalId, goalName) => {
    let url = `/analytics/${websiteId}?period=${period}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    existingFilters.forEach(f => {
      url += `&f=${encodeURIComponent(f)}`;
    });
    url += `&f=is,goal,${goalId}:${encodeURIComponent(goalName)}`;
    navigate(url);
  };

  // Fetch goals and conversions
  const fetchData = async () => {
    // publicMode has no token; backend allows unauthenticated reads on is_public sites.
    if (!websiteId) return;
    
    // Create a unique key for this fetch request
    const fetchKey = `${websiteId}-${period}-${startDate}-${endDate}-${channel}-${location}-${device}-${os}-${browser}-${pageFiltersKey}-${filtersKey}`;
    
    // Skip if already fetching or same request
    if (fetchingRef.current || lastFetchKeyRef.current === fetchKey) {
      return;
    }
    
    fetchingRef.current = true;
    lastFetchKeyRef.current = fetchKey;

    setLoading(true);
    try {
      const goalsRes = await axios.get(`${BACKEND_URL}/api/goals/${websiteId}`, {
        headers: authHeaders
      });
      setGoals(goalsRes.data);

      let convUrl = `${BACKEND_URL}/api/analytics/${websiteId}/goal-conversions?period=${period}`;
      if (startDate && endDate) convUrl += `&start_date=${startDate}&end_date=${endDate}`;
      if (channel) convUrl += `&channel=${encodeURIComponent(channel)}`;
      if (location) convUrl += `&location=${encodeURIComponent(location)}`;
      if (device) convUrl += `&device=${encodeURIComponent(device)}`;
      if (os) convUrl += `&os=${encodeURIComponent(os)}`;
      if (browser) convUrl += `&browser=${encodeURIComponent(browser)}`;
      
      if (pageFilters?.length > 0) {
        pageFilters.forEach(filter => {
          convUrl += `&f=${filter.condition},${filter.type},${encodeURIComponent(filter.value)}`;
        });
      }
      if (existingFilters?.length > 0) {
        existingFilters.forEach(f => {
          convUrl += `&f=${encodeURIComponent(f)}`;
        });
      }

      const convRes = await axios.get(convUrl, {
        headers: authHeaders
      });
      setConversions(convRes.data.items || []);

      // Fetch funnels
      const funnelsRes = await axios.get(`${BACKEND_URL}/api/funnels/${websiteId}`, {
        headers: authHeaders
      });
      const funnelsData = funnelsRes.data || [];
      setFunnels(funnelsData);

      // Fetch stats for each funnel
      const statsPromises = funnelsData.map(funnel => {
        let funnelStatsUrl = `${BACKEND_URL}/api/funnels/${websiteId}/${funnel.id}/stats?period=${period}`;
        if (startDate && endDate) funnelStatsUrl += `&start_date=${startDate}&end_date=${endDate}`;
        if (channel) funnelStatsUrl += `&channel=${encodeURIComponent(channel)}`;
        if (location) funnelStatsUrl += `&location=${encodeURIComponent(location)}`;
        if (device) funnelStatsUrl += `&device=${encodeURIComponent(device)}`;
        if (os) funnelStatsUrl += `&os=${encodeURIComponent(os)}`;
        if (browser) funnelStatsUrl += `&browser=${encodeURIComponent(browser)}`;
        if (pageFilters?.length > 0) {
          pageFilters.forEach(filter => {
            funnelStatsUrl += `&f=${filter.condition},${filter.type},${encodeURIComponent(filter.value)}`;
          });
        }
        if (existingFilters?.length > 0) {
          existingFilters.forEach(f => {
            funnelStatsUrl += `&f=${encodeURIComponent(f)}`;
          });
        }
        
        return axios.get(funnelStatsUrl, {
          headers: authHeaders
        }).then(res => ({ [funnel.id]: res.data })).catch(() => ({ [funnel.id]: null }));
      });
      
      const statsResults = await Promise.all(statsPromises);
      const combinedStats = statsResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setFunnelStats(combinedStats);
    } catch (err) {
      console.error('Error fetching goal data:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  // Fetch custom properties
  const fetchPropertiesData = async () => {
    // publicMode has no token; backend allows unauthenticated reads on is_public sites.
    if (!websiteId) return;
    
    setPropertiesLoading(true);
    try {
      let eventsUrl = `${BACKEND_URL}/api/analytics/${websiteId}/property-events?period=${period}`;
      if (startDate && endDate) {
        eventsUrl += `&start_date=${startDate}&end_date=${endDate}`;
      }
      
      const eventsRes = await axios.get(eventsUrl, {
        headers: authHeaders
      });
      setPropertyEvents(eventsRes.data?.items || []);
    } catch (err) {
      console.error('Error fetching properties data:', err);
      setPropertyEvents([]);
    } finally {
      setPropertiesLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [websiteId, period, startDate, endDate, channel, location, device, os, browser, token, pageFiltersKey, filtersKey]);

  useEffect(() => {
    if (activeTab === 'properties') {
      fetchPropertiesData();
    }
  }, [activeTab, websiteId, period, startDate, endDate, token]);

  // Filter conversions based on active goal filter (from URL param or pageFilters)
  let filteredConversions = conversions;
  if (activeGoalFilter) {
    filteredConversions = conversions.filter(conv => conv.id === activeGoalFilter.id);
  } else if (goal) {
    const goalId = goal.includes(':') ? goal.split(':')[0] : goal;
    filteredConversions = conversions.filter(conv => conv.id === goalId);
  }

  // Modal data
  useEffect(() => {
    if (!showModal || !conversions.length) {
      setModalData([]);
      return;
    }

    let dataToShow = conversions;
    if (activeGoalFilter) {
      dataToShow = conversions.filter(conv => conv.id === activeGoalFilter.id);
    } else if (goal) {
      const goalId = goal.includes(':') ? goal.split(':')[0] : goal;
      dataToShow = conversions.filter(conv => conv.id === goalId);
    }

    const transformedData = dataToShow.map(conv => ({
      goal: conv.name,
      unique_visitors: conv.unique_visitors,
      total_conversions: conv.total_conversions,
      conversion_rate: conv.conversion_rate
    }));

    setModalData(transformedData);
    setModalLoading(false);
  }, [showModal, conversions, goal]);

  const filteredModalData = modalData.filter(item => {
    if (!searchQuery.trim()) return true;
    return item.goal?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const sortedModalData = [...filteredModalData].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const displayData = filteredConversions.slice(0, limit);
  
  // Calculate max for bar width
  const maxUniques = Math.max(...displayData.map(d => d.unique_visitors || 0), 1);

  return (
    <>
      <div className="bg-white rounded-2xl" style={{ boxShadow: '0 2px 12px rgba(15, 23, 42, 0.06)' }}>
        {/* Header with Tabs */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <button
              className={`text-sm font-semibold ${activeTab === 'goals' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              onClick={() => setActiveTab('goals')}
            >
              GOALS
            </button>
            <button
              className={`text-sm font-semibold ${activeTab === 'properties' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              onClick={() => setActiveTab('properties')}
            >
              PROPERTIES
            </button>
            <button
              className={`text-sm font-semibold ${activeTab === 'funnels' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              onClick={() => setActiveTab('funnels')}
            >
              FUNNELS
            </button>
            <button
              className={`text-sm font-semibold ${activeTab === 'explore' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              onClick={() => setActiveTab('explore')}
            >
              EXPLORE
            </button>
          </div>
          {displayData.length > 0 && activeTab === 'goals' && (
            <button 
              onClick={() => setShowModal(true)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          )}
        </div>

        {/* Underline indicator */}
        <div className="px-4">
          <div className="w-8 h-0.5 bg-indigo-600" style={{
            transform: `translateX(${activeTab === 'goals' ? '0' : activeTab === 'properties' ? '80px' : activeTab === 'funnels' ? '180px' : '270px'})`
          }}></div>
        </div>

        {/* Goals Tab */}
        {activeTab === 'goals' && (
          <div className="p-4">
            {/* Column Headers */}
            <div className="flex items-center text-xs text-gray-500 mb-2 px-2">
              <div className="flex-1">Goal</div>
              <div className="w-20 text-right">Uniques</div>
              <div className="w-20 text-right">Total</div>
              <div className="w-16 text-right">CR</div>
            </div>

            {loading && (
              <div className="py-8 text-center text-gray-500">Loading...</div>
            )}

            {!loading && displayData.length === 0 && (
              <div className="py-8 text-center">
                <p className="text-gray-500 mb-4">No goals created yet</p>
                <button
                  onClick={() => navigate(`/settings/${websiteId}`)}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium inline-flex items-center gap-1"
                >
                  <Settings className="w-4 h-4" />
                  Set up goals in settings
                </button>
              </div>
            )}

            {!loading && displayData.map((conv, idx) => {
              const barWidth = (conv.unique_visitors / maxUniques) * 100;
              return (
                <div
                  key={idx}
                  className="relative flex items-center py-3 px-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors group"
                  onClick={() => handleGoalClick(conv.id, conv.name)}
                >
                  {/* Background Bar */}
                  <div 
                    className="absolute left-2 top-1 bottom-1 rounded"
                    style={{ 
                      width: `${Math.min(barWidth, 70)}%`,
                      backgroundColor: 'rgba(251, 207, 191, 0.5)'
                    }}
                  />
                  
                  {/* Content */}
                  <div className="relative flex-1 z-10">
                    <span className="text-sm text-gray-900">{conv.name}</span>
                  </div>
                  <div className="relative w-20 text-right z-10">
                    <span className="text-sm font-medium text-gray-900">{conv.unique_visitors?.toLocaleString() || 0}</span>
                  </div>
                  <div className="relative w-20 text-right z-10">
                    <span className="text-sm text-gray-600">{conv.total_conversions?.toLocaleString() || 0}</span>
                  </div>
                  <div className="relative w-16 text-right z-10">
                    <span className="text-sm text-gray-600">{conv.conversion_rate}%</span>
                  </div>
                </div>
              );
            })}

            {filteredConversions.length > limit && (
              <button 
                className="w-full mt-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                onClick={() => setLimit(prev => prev + 10)}
              >
                Show more
              </button>
            )}
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div className="p-4">
            <div className="flex items-center text-xs text-gray-500 mb-2 px-2">
              <div className="flex-1">Property / Event</div>
              <div className="w-20 text-right">Visitors</div>
              <div className="w-20 text-right">Events</div>
            </div>

            {propertiesLoading && (
              <div className="py-8 text-center text-gray-500">Loading...</div>
            )}

            {!propertiesLoading && propertyEvents.length === 0 && (
              <div className="py-8 text-center">
                <p className="text-gray-500 mb-2">No custom events tracked yet</p>
                <p className="text-xs text-gray-400 mb-4">
                  Track events using: <code className="bg-gray-100 px-2 py-1 rounded">window.trackEvent('event_name')</code>
                </p>
              </div>
            )}

            {!propertiesLoading && propertyEvents.map((event, idx) => {
              const maxEvents = Math.max(...propertyEvents.map(e => e.unique_visitors || 0), 1);
              const barWidth = (event.unique_visitors / maxEvents) * 100;
              return (
                <div
                  key={idx}
                  className="relative flex items-center py-3 px-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div 
                    className="absolute left-2 top-1 bottom-1 rounded"
                    style={{ 
                      width: `${Math.min(barWidth, 70)}%`,
                      backgroundColor: 'rgba(199, 210, 254, 0.5)'
                    }}
                  />
                  <div className="relative flex-1 z-10 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 bg-indigo-100 text-indigo-600 rounded text-xs font-medium">E</span>
                    <span className="text-sm text-gray-900">{event.event_name}</span>
                  </div>
                  <div className="relative w-20 text-right z-10">
                    <span className="text-sm font-medium text-gray-900">{event.unique_visitors?.toLocaleString() || 0}</span>
                  </div>
                  <div className="relative w-20 text-right z-10">
                    <span className="text-sm text-gray-600">{event.total_events?.toLocaleString() || 0}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Funnels Tab */}
        {activeTab === 'funnels' && (
          <div className="p-4">
            <div className="flex items-center text-xs text-gray-500 mb-2 px-2">
              <div className="flex-1">Funnel</div>
              <div className="w-20 text-right">Visitors</div>
              <div className="w-20 text-right">Conv. rate</div>
            </div>

            {loading && (
              <div className="py-8 text-center text-gray-500">Loading...</div>
            )}

            {!loading && funnels.length === 0 && (
              <div className="py-8 text-center">
                <p className="text-gray-500 mb-4">No funnels created yet</p>
                <button
                  onClick={() => navigate(`/settings/${websiteId}`)}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium inline-flex items-center gap-1"
                >
                  <Settings className="w-4 h-4" />
                  Set up funnels in settings
                </button>
              </div>
            )}

            {!loading && funnels.map((funnel, idx) => {
              const stats = funnelStats[funnel.id];
              const visitors = stats?.steps?.[0]?.visitors || 0;
              const maxVisitors = Math.max(...funnels.map(f => funnelStats[f.id]?.steps?.[0]?.visitors || 0), 1);
              const barWidth = (visitors / maxVisitors) * 100;
              return (
                <div
                  key={idx}
                  className="px-2 py-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {/* Top row — matches Properties tab layout */}
                  <div className="relative flex items-center">
                    <div
                      className="absolute left-0 top-0 bottom-0 rounded"
                      style={{
                        width: `${Math.min(barWidth, 70)}%`,
                        backgroundColor: 'rgba(199, 210, 254, 0.5)'
                      }}
                    />
                    <div className="relative flex-1 z-10 flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5 bg-indigo-100 text-indigo-600 rounded text-xs font-medium">F</span>
                      <span className="text-sm font-medium text-gray-900">{funnel.name}</span>
                    </div>
                    <div className="relative w-20 text-right z-10">
                      <span className="text-sm font-medium text-gray-900">{visitors.toLocaleString()}</span>
                    </div>
                    <div className="relative w-20 text-right z-10">
                      <span className="text-sm font-semibold text-green-600">{stats?.conversion_rate || 0}%</span>
                    </div>
                  </div>

                  {/* Step pills */}
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-2 flex-wrap">
                    {funnel.steps?.map((step, i) => (
                      <span key={i} className="flex items-center">
                        <span className="bg-gray-100 px-2 py-1 rounded">{step.page_path}</span>
                        {i < funnel.steps.length - 1 && (
                          <ArrowRight className="w-3 h-3 mx-1 text-gray-400" />
                        )}
                      </span>
                    ))}
                  </div>

                  {/* Per-step stats */}
                  {stats?.steps && (
                    <div className="flex items-center gap-4 mt-3 flex-wrap">
                      {stats.steps.map((step, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Step {i + 1}:</span>
                          <span className="text-sm font-medium">{step.visitors?.toLocaleString() || 0}</span>
                          {i > 0 && step.dropoff_percentage > 0 && (
                            <span className="text-xs text-red-500">(-{step.dropoff_percentage}%)</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Explore Tab — interactive user-journeys path exploration */}
        {activeTab === 'explore' && (
          <div className="p-4">
            <ExploreJourneys
              websiteId={websiteId}
              token={token}
              period={period}
              startDate={startDate}
              endDate={endDate}
              channel={channel}
              location={location}
              device={device}
              os={os}
              browser={browser}
              goal={goal}
              existingFilters={existingFilters}
            />
          </div>
        )}
      </div>

      {/* Details Modal */}
      <DetailsModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSearchQuery('');
          setSortConfig({ key: 'unique_visitors', direction: 'desc' });
        }}
        websiteId={websiteId}
        period={period}
        startDate={startDate}
        endDate={endDate}
        channel={channel}
        location={location}
        device={device}
        os={os}
        browser={browser}
        title="Goal Conversions - All Results"
        type="goals"
        data={sortedModalData}
        loading={modalLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortConfig={sortConfig}
        onSort={handleSort}
      />
    </>
  );
};

export default GoalConversions;
